import { RefObject } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNotification, useTranslate } from "@refinedev/core";

import { ModalRef } from "@shared/components";
import { useEditSubscriptionMutation } from "@modules/subscriptions/services/mutations";
import { SubscriptionFormValues, subscriptionSchema } from "@modules/subscriptions/validations";
import { listSubscriptionsQueryKey } from "@modules/subscriptions/services/queries/listSubscriptions/query";
import { isValidCPF } from "@modules/users/utils/isValidCPF";

export function useEditSubscriptionModal(modalRef: RefObject<ModalRef>, onComplete?: () => void) {
  const queryClient = useQueryClient();
  const translate = useTranslate();
  const { open } = useNotification();

  const methods = useForm<SubscriptionFormValues>({
    resolver: zodResolver(subscriptionSchema({
      cpf: {
        validate: async (value) => value ? isValidCPF(value) : true,
        errorMessage: translate("v1/user_admin.errors.invalidCPF"),
      },
    })),
    mode: "onBlur"
  });

  const { mutateAsync, isPending } = useEditSubscriptionMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listSubscriptionsQueryKey,
        refetchType: 'all',
      });

      open?.({
        type: "success",
        message: translate("subscription_admin.success.edit"),
      });

      onComplete?.();
      modalRef.current?.close();
    },
  });

  const handleSubmit = async (data: SubscriptionFormValues) => {
    const subscriptionId = modalRef.current?.getId();
    await mutateAsync({ id: subscriptionId as string, data });
  }

  return {
    methods,
    isPending,
    handleSubmit,
  };
}
